import { createApi } from '@reduxjs/toolkit/query/react';

import { TREATMENT_AUDIT_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

/**
 * A mapped audit log object.
 * @typedef {Object} MappedAuditLog
 * @property {string} logId - The log ID.
 * @property {string} eventType - The event type.
 * @property {string} title - The title.
 * @property {string} description - The description.
 * @property {string} upn - The UPN.
 * @property {string} reason - The reason.
 * @property {string} source - The source.
 * @property {string} timestamp - The timestamp.
 * @property {boolean} hasEventJSON - Whether the log has event JSON.
 */

export const treatmentAuditApi = createApi({
    baseQuery: getBaseQuery(TREATMENT_AUDIT_API_URL),
    reducerPath: 'treatmentAuditApi',
    endpoints: (build) => ({
        getTreatmentAuditLogs: build.query({
            query: ({ treatmentId }) => treatmentId,
            /**
             * Maps a list of audit logs from the API to a list of audit log objects.
             * @param {Array<Object>} response List of logs for the treatment.
             * @returns {Array<MappedAuditLog>} The mapped list of audit log objects.
             */
            transformResponse: (response) =>
                response.map((log) => ({
                    logId: log.log_id,
                    eventType: log.event_type,
                    title: log.log_title,
                    description: log.log_description,
                    upn: log.log_upn,
                    reason: log.log_reason,
                    source: log.log_source,
                    timestamp: log.log_timestamp,
                    hasEventJSON: log.has_event_json,
                })),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving treatment audit entries.', err);
                }
            },
        }),
        getAuditLogEventJSON: build.query({
            query: ({ logId }) => `event_json/${logId}`,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving raw audit entry.',
            ),
        }),
    }),
});

export const { useGetTreatmentAuditLogsQuery, useGetAuditLogEventJSONQuery } = treatmentAuditApi;
